import { date } from '@ipfabric/utils';
import { constVoid, pipe } from 'fp-ts/lib/function';
import { createLastNSum } from 'utils/math/LastNSum';
import type {
  AnimationFn,
  RunningAnimation,
} from 'utils/pixi/animation/AnimationController';

function createSingleFrameAnimation(
  renderPromise: Promise<void>,
): RunningAnimation {
  return {
    afterFrame: (cb) => renderPromise.then(cb),
    cancel: constVoid,
    forceFinish: constVoid,
    getElapsedTime: () => 0,
    getPhase: () => 1,
    promise: Promise.resolve(),
  };
}

interface SuspendAnimateOptions {
  checkFrames: number;
  handbrakeLowFrameTime: number;
  maxFrameTime: number;
  retryAfter: number;
  retryProlongFactor: number;
}

const defaultOptions: SuspendAnimateOptions = {
  /**
   * Calculate average frame time on this number of frames
   */
  checkFrames: 3,
  /**
   * Stop animation if average frame time exceeds this number
   */
  maxFrameTime: 100,
  /**
   * Stop animation immediately if single frame time exceeds this number
   */
  handbrakeLowFrameTime: 300,
  /**
   * Re-enable stopped animation after this time
   */
  retryAfter: 10000,
  /**
   * Multiply retryAfter interval by this constant after each disable
   *
   * Note: retryAfter is reset after checkFrames * 2 frames are rendered in accepted time
   */
  retryProlongFactor: 1.5,
};

export function suspendAnimateOnLowFps(
  animate: AnimationFn,
  requestRender: (cb: () => void) => Promise<void>,
  options?: Partial<SuspendAnimateOptions>,
): AnimationFn {
  let disabledUntil = 0;
  const o = { ...defaultOptions, ...options };
  const maxFrameTimeSpan = o.maxFrameTime * o.checkFrames;
  let { retryAfter } = o;

  return (lengthMilliseconds, apply) => {
    let last = date.getTs();
    let okFrames = 0;
    const lastNFramesRenderTime = createLastNSum(o.checkFrames, 0);
    if (disabledUntil > last) {
      return pipe(() => apply(1), requestRender, createSingleFrameAnimation);
    }
    let current: number;

    const disableAnimation = () => {
      const disableAt = date.getTs();
      if (disabledUntil > disableAt) {
        // After the animation is disabled, one more frame is rendered to get it to the final state.
        // This condition prevents double disabling and therefore double application of o.retryProlongFactor.
        return;
      }
      disabledUntil = disableAt + retryAfter;
      retryAfter *= o.retryProlongFactor;
      okFrames = 0;
      lastNFramesRenderTime.reset();

      runningAnimation.forceFinish();
    };

    const applyWithMeter = (phase: number) => {
      runningAnimation.afterFrame(() => {
        current = date.getTs();
        if (last + o.handbrakeLowFrameTime < current) {
          disableAnimation();
          return;
        }
        lastNFramesRenderTime.push(current - last);
        if (lastNFramesRenderTime.getSum() > maxFrameTimeSpan) {
          disableAnimation();
          return;
        }
        okFrames += 1;
        if (okFrames === o.checkFrames * 2) {
          // reset prolonging when at least 2*checkFrames rendered fast
          retryAfter = o.retryAfter;
        }
        last = current;
      });
      apply(phase);
    };

    const runningAnimation = animate(lengthMilliseconds, applyWithMeter);
    return runningAnimation;
  };
}
